
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import TimelineProvider from "@/components/app/global/timelineComponent/timelineProvider";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import eventsMap from "@/components/app/global/timelineComponent/events";

// Types
import {
  IWebhookEventProviderData,
  IWebhookEventProviderMethods
} from "@/models/providers/webhookEvents";
import { IState } from "@/store";
import { IWebhookEvent, IWebhookEventPayload } from "@/models/webhooks";
import { AdminRoutes } from "@/data/enums/router";
import { ITimelineMappedEvent } from "@/models/providers/timeline";

@Component({
  computed: {
    ...mapState({
      webhookEventScope(state: IState, getters) {
        return getters[`data/${DataModules.WEBHOOKS_EVENTS}/scope`](
          this.webhookEventId
        );
      },
      webhookEvent(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.WEBHOOKS_EVENTS,
          scope: this.webhookEventScope
        });
      },
      webhookEventPayloadScope(state: IState, getters) {
        return getters[`data/${DataModules.WEBHOOKS_EVENTS}/payloadScope`](
          this.webhookEventId
        );
      },
      webhookEventPayload(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.WEBHOOKS_EVENTS,
          scope: this.webhookEventPayloadScope
        });
      }
    })
  }
})
export default class WebhookEventProvider extends Mixins(
  RequestPromises,
  TimelineProvider
) {
  // Map State

  public webhookEvent!: IWebhookEvent;
  public webhookEventScope!: string;
  public webhookEventPayload!: IWebhookEventPayload;
  public webhookEventPayloadScope!: string;

  // Props

  @Prop({ required: true })
  public webhookEventId!: IWebhookEvent["id"];

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: "VWEM1" })
  public declare id: string;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  public webhookEditRouteName = AdminRoutes.WEBHOOKS_SETTINGS_WEBHOOK_EDIT;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.WEBHOOKS_EVENTS,
      scope: this.webhookEventScope,
      vm: this
    });
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.WEBHOOKS_EVENTS,
      scope: this.webhookEventPayloadScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getWebhookEvent(),
        this.getWebhookEventPayload()
      ]);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getWebhookEvent() {
    return this.$store.dispatch(`data/${DataModules.WEBHOOKS_EVENTS}/get`, {
      id: this.webhookEventId,
      scope: this.webhookEventScope,
      params: {
        with: ["hook_log.hook", "hook_log.object"].join(",")
      },
      vm: this,
      ignoreStored: this.isReloading
    });
  }

  public getWebhookEventPayload() {
    return this.$store.dispatch(
      `data/${DataModules.WEBHOOKS_EVENTS}/getEventPayload`,
      {
        id: this.webhookEventId,
        scope: this.webhookEventPayloadScope,
        vm: this,
        ignoreStored: this.isReloading
      }
    );
  }

  // Computed

  public get webhookEventLog() {
    const event = this.webhookEvent.hook_log;
    const code = _.get(event, "hook.code", "");
    const eventMap = _.get(eventsMap, code, {
      event: () => ({
        timestamp: event.created_at,
        message: _.get(event, "hook.name", _.get(event, "object_type"))
      })
    });

    return {
      ..._.omit(eventMap.event(event), ["icon"]),
      ..._.pick(event, [
        "id",
        "actor",
        "object",
        "object_type",
        "object_brand_id",
        "object_id",
        "hook"
      ]),
      sourceEvent: event,
      isHidden: this.hiddenEvents[event.id] || false,
      wasHidden: this.pastHiddenEvents[event.id] || false,
      index: 0
    } as ITimelineMappedEvent;
  }

  // Guarding

  public get guardFunctionalities() {
    return ["get_webhook_event"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("webhookEventProviderData")
  public webhookEventProviderData(): IWebhookEventProviderData {
    return {
      guardFunctionalities: this.guardFunctionalities,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      webhook: this.webhookEvent.endpoint,
      webhookEditRouteName: this.webhookEditRouteName,
      webhookEvent: this.webhookEvent,
      webhookEventLog: this.webhookEventLog,
      webhookEventPayload: this.webhookEventPayload
    };
  }

  @Provide("webhookEventProviderMethods")
  public get webhookEventProviderMethods(): IWebhookEventProviderMethods {
    return {
      reloadData: this.reloadData
    };
  }

  // Render function
  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $webhookEventData: this.webhookEventProviderData(),
        $webhookEventMethods: this.webhookEventProviderMethods
      })
    );
  }
}
